/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';
import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';

import type { TooltipProps } from '../../types';
import './Tooltip.less';

let _tooltipIdCounter = 0;

const Tooltip = ({
  content,
  children,
  placement = 'top',
  delay = 250,
  disabled = false,
  open: controlledOpen,
  onOpenChange,
  className,
}: TooltipProps) => {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isOpen = isControlled ? !!controlledOpen : internalOpen;

  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltipId = React.useMemo(() => `ds-tooltip-${++_tooltipIdCounter}`, []);

  const clearPendingTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const commitOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  const requestOpen = React.useCallback(() => {
    if (disabled) return;
    clearPendingTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      commitOpen(true);
    }, delay);
  }, [disabled, delay, clearPendingTimer, commitOpen]);

  const requestClose = React.useCallback(() => {
    clearPendingTimer();
    commitOpen(false);
  }, [clearPendingTimer, commitOpen]);

  React.useEffect(() => clearPendingTimer, [clearPendingTimer]);

  const { refs, floatingStyles } = useFloating({
    placement,
    strategy: 'fixed',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  if (!React.isValidElement(children)) {
    return null;
  }

  const childProps = children.props as Record<string, unknown>;

  const trigger = React.cloneElement(children, {
    onMouseEnter: (e: React.MouseEvent) => {
      (childProps.onMouseEnter as ((e: React.MouseEvent) => void) | undefined)?.(e);
      requestOpen();
    },
    onMouseLeave: (e: React.MouseEvent) => {
      (childProps.onMouseLeave as ((e: React.MouseEvent) => void) | undefined)?.(e);
      requestClose();
    },
    onFocus: (e: React.FocusEvent) => {
      (childProps.onFocus as ((e: React.FocusEvent) => void) | undefined)?.(e);
      requestOpen();
    },
    onBlur: (e: React.FocusEvent) => {
      (childProps.onBlur as ((e: React.FocusEvent) => void) | undefined)?.(e);
      requestClose();
    },
    'aria-describedby': isOpen ? tooltipId : (childProps['aria-describedby'] as string | undefined),
  } as Partial<React.HTMLAttributes<HTMLElement>>);

  return (
    <span ref={refs.setReference} className={cx('tooltip-wrapper', className)}>
      {trigger}
      {isOpen && (
        <FloatingPortal>
          <span
            ref={refs.setFloating}
            id={tooltipId}
            role="tooltip"
            className={cx('tooltip__body', `tooltip__body--${placement}`)}
            style={floatingStyles}>
            {content}
          </span>
        </FloatingPortal>
      )}
    </span>
  );
};

Tooltip.displayName = 'Tooltip';

export default Tooltip;
